import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { usePrevious } from "react-use";
import { Box } from "@mui/material";

import { NotificationBannerSlot } from "ui/NotificationBanner/NotificationBannerSlot";

import {
  injectIntoVersionConfig,
  selectAllVersionGroupConfigs,
  selectAllVersionLayers,
  selectCurrentVersionStatus,
  useGetAccountProjects,
  useGetIconsQuery,
  useGetLayersQuery,
  useGetVersionConfigQuery,
  useGetVersionQuery,
  useGetVersionsQuery,
  useGetVersionStatusQuery,
  useLazyGetVersionCostBinRangesQuery,
} from "fond/api";
import { AsyncOperationState } from "fond/async/redux";
import LayerFilterProvider from "fond/cityPlanner/LayerFilterProvider";
import { HistoryProvider } from "fond/history";
import { useEntityUtil } from "fond/hooks/useEntityUtil";
import useEntityViews from "fond/hooks/useEntityViews";
import { useUpdateDocTitle } from "fond/hooks/useUpdateDocTitle";
import ImportModal from "fond/import/modal";
import { commentsConfig } from "fond/layers";
import LayoutProvider from "fond/layout/LayoutProvider";
import MapProvider from "fond/map/MapProvider";
import { NavigateError } from "fond/navigation/NavigateError";
import { useNavigateError } from "fond/navigation/useNavigateError";
import { PageMenu } from "fond/project/pageMenu";
import ProjectMapPage from "fond/project/ProjectMapPage";
import {
  closeImportModal,
  closeProject,
  dismissLoadProjectError,
  getCurrentProject,
  loadProject,
  Modals,
  setLayersVisibility,
  setVersion,
} from "fond/project/redux";
import { getComments } from "fond/redux/comments";
import TopBar from "fond/topBar/TopBar";
import { Store } from "fond/types";
import { costToServeRanges } from "fond/utils/costToServeTransformers";
import { useAppDispatch } from "fond/utils/hooks";
import { Actions as PermissionActions, permissionCheck } from "fond/utils/permissions";
import { getProjectCurrency, getProjectName, isDesignProject, isPlannerProject, projectCanHaveVersions } from "fond/utils/project";
import { BlockSpinner, ErrorModal, StackedNavigationProvider } from "fond/widgets";

const ProjectPage: React.FC = () => {
  useEntityViews();
  const dispatch = useAppDispatch();
  const navigateError = useNavigateError();
  useGetAccountProjects();
  const { uuid: projectId } = useParams<"uuid">();
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const currentStatus = useSelector((state: Store) => selectCurrentVersionStatus(state));
  const versionId = useSelector((state: Store) => state.project.versionId);
  const previousVersionId = usePrevious(versionId);
  const loadProjectStatus = useSelector((state: Store) => state.project.loadProjectStatus);
  const loadDataStatus = useSelector((state: Store) => state.project.loadDataStatus);
  const modal = useSelector((state: Store) => state.project.modal);
  const [loaded, setLoaded] = useState(false);
  const {
    data: versions,
    isError: isErrorVersions,
    isSuccess: versionDataLoaded,
    refetch: refetchVersions,
  } = useGetVersionsQuery(projectId as string, { skip: !projectId });
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const { data: status } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const [getCostBinRanges] = useLazyGetVersionCostBinRangesQuery();
  const { isSuccess: iconsLoaded } = useGetIconsQuery(undefined);
  const layerConfigs = useSelector((state: Store) => selectAllVersionLayers(state, versionId));
  const groupConfigs = useSelector((state: Store) => selectAllVersionGroupConfigs(state, versionId));
  const { data: config, isSuccess: configDataLoaded } = useGetVersionConfigQuery(versionId, { skip: !versionId, refetchOnMountOrArgChange: true });
  const { isSuccess: layersLoaded } = useGetLayersQuery(versionId, { skip: !versionId });
  const { getEntityLabel } = useEntityUtil();

  const allLoaded = loaded && layersLoaded && iconsLoaded && versionDataLoaded && configDataLoaded && !!project;
  const projectName = getProjectName(project);
  const projectCurrency = getProjectCurrency(project);
  const projectType = project ? getEntityLabel(project, "long") : "";
  const showDesignPanel =
    (isPlannerProject(project) || isDesignProject(project)) &&
    !status?.HasSolution &&
    permissionCheck(project?.Permission?.Level, PermissionActions.PROJECT_EDIT);
  const MAX_DISPLAY_BINS_COUNT = 5;

  const windowTitle = useMemo(() => {
    return `${projectName || "Untitled Project"} - ${version?.Name || "unknown version"} - ${projectType}`;
  }, [projectName, projectType, version?.Name]);
  useUpdateDocTitle(windowTitle);

  useEffect(() => {
    return () => {
      dispatch(closeProject());
    };
  }, [dispatch]);

  // Ensure the version root configuration always contains the comments Group, Layer, and Styles.
  useEffect(() => {
    // Configuration IDs returned by the backend are always UUIDs, so there's no way that a user could have
    // created a configuration having `projectComments` as its ID.
    if (config && !config.MapChildren.some((id) => config.Data.entities[id]?.ID === "projectComments")) {
      injectIntoVersionConfig(dispatch, versionId, commentsConfig);
    }
  }, [dispatch, config, versionId]);

  /**
   * Once a design is complete add the cost to serve layers
   */
  useEffect(() => {
    if (version && configDataLoaded && currentStatus?.Status === "Complete") {
      getCostBinRanges(version.ID).then((result) => {
        if (result.data) {
          const transform = costToServeRanges(result.data, MAX_DISPLAY_BINS_COUNT, projectCurrency);
          // Only add the layers if the bin ranges contained a value layer configuration
          // e.g. costs & ranges were generated during the design.
          if (transform.some((entity) => entity.Type === "LAYER")) {
            injectIntoVersionConfig(dispatch, version.ID, transform);
          }
        }
      });
    }
  }, [currentStatus?.Status, config, configDataLoaded, dispatch, version, getCostBinRanges, projectCurrency]);

  /**
   * When the layers and group load we inject any missing items into
   * the layer visibility record to allow for correct layer visibility toggling.
   */
  useEffect(() => {
    if (layerConfigs && groupConfigs) {
      dispatch(setLayersVisibility({ projectId, layerConfigs, groupConfigs }));
    }
  }, [dispatch, layerConfigs, groupConfigs, projectId]);

  /**
   * View the latest version whenever the versions change.
   */
  useEffect(() => {
    if (versionId && projectCanHaveVersions(project)) {
      dispatch(setVersion(versionId));
    } else if (projectId && versions && versions.ids?.length > 0) {
      dispatch(setVersion(versions.ids[0]));
    }
  }, [dispatch, projectId, project, versions, versionId]);

  /**
   * Whenever the version changes, reload the project and comments.
   *
   * This is necessary for planner projects where version-state is stored in a project-scoped manner.
   */
  useEffect(() => {
    if (projectId && versionId) {
      const forceRefetch = versionId !== previousVersionId || layerConfigs.length === 0;
      const load = async () => {
        if (loaded) {
          // No need to fetch comments again.
          await dispatch(loadProject({ uuid: projectId, forceRefetch }));
        } else {
          await Promise.all([dispatch(loadProject({ uuid: projectId, forceRefetch })), dispatch(getComments(projectId))]);
          setLoaded(true);
        }
      };
      load();
    }
  }, [dispatch, loaded, previousVersionId, layerConfigs, projectId, versionId]);

  /**
   * Callback for handling retry after an error during loading
   */
  const handleOnError = () => {
    refetchVersions();
    dispatch(dismissLoadProjectError(navigateError));
  };

  // If project could not be found, there are no versions, or fetching of versions failed due to user having no access to project, we redirect
  if (loadProjectStatus === AsyncOperationState.notFound || (versions && versions.ids?.length === 0) || isErrorVersions) {
    return <NavigateError to="/404" resourceType="PROJECT" />;
  }

  return (
    <MapProvider>
      <Box display="flex" height="100%" width="100%" flexDirection="column">
        {loadProjectStatus === AsyncOperationState.failure && (
          <ErrorModal message="There was a problem loading the project. Please try again." onClose={handleOnError} />
        )}
        {loadDataStatus === AsyncOperationState.failure && (
          <ErrorModal message="There was a problem loading the project data. Please try again." onClose={handleOnError} />
        )}
        {allLoaded ? (
          <LayoutProvider type="project" showDesignPanel={showDesignPanel}>
            <StackedNavigationProvider>
              <HistoryProvider>
                <LayerFilterProvider versionId={versionId}>
                  <Box>
                    <NotificationBannerSlot isFixed={false} />
                    <TopBar />
                    <PageMenu type="project" />
                  </Box>
                  <ProjectMapPage />
                </LayerFilterProvider>
              </HistoryProvider>
            </StackedNavigationProvider>
          </LayoutProvider>
        ) : (
          <Box display="flex" height="100%">
            <BlockSpinner />
          </Box>
        )}
        {modal === Modals.import && <ImportModal onClose={() => dispatch(closeImportModal())} />}
      </Box>
    </MapProvider>
  );
};

export default ProjectPage;
